import { tabsCaptionStrongSx, tabsCaptionSx } from '../../../styles/commonStyles';
import { IOnDragEndEventArgs } from '../../../typings/draggable/onDragEndEventArgs';
import { ISemanticVocabulary } from '../../../typings/semantic/semanticVocabulary';
import { IVocabColorPalette } from '../../../typings/semantic/vocabColorPalette';
import RupTooltip from '../../rupTooltip';
import ColoredDraggableVocabulary from './coloredDraggableVocabulary';
import { Collapse, Stack } from '@mui/material';
import Box from '@mui/material/Box';
import { Colors, Icons } from 'copyright-clearance-center-common-ui';
import { RupSwitch } from 'copyright-clearance-center-common-ui-react';
import React from 'react';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';

export interface IEnrichmentSettingsProps {
	vocabularies: ISemanticVocabulary[];
	vocabColorPalette: IVocabColorPalette;
	switchable: boolean;
	semanticSearchEnabled: boolean;
	onEnrichmentEnabledChanged?: (enabled: boolean) => void;
	onVocabOrderChanged?: (orderForDb: string, orderForUi: number[], orderedKeys: string[]) => void;
	isLoading: boolean;
}

const EnrichmentSettings: React.FunctionComponent<IEnrichmentSettingsProps> = props => {
	const [enrichmentEnabled, setEnrichmentEnabled] = React.useState(props.semanticSearchEnabled);
	const [vocabularies, setVocabularies] = React.useState(props.vocabularies ?? []);
	const [switchable] = React.useState(props.switchable ?? true);

	React.useEffect(() => {
		if (props.onEnrichmentEnabledChanged) {
			props.onEnrichmentEnabledChanged(enrichmentEnabled);
		}
	}, [enrichmentEnabled]);

	React.useEffect(() => {
		if (props.onVocabOrderChanged) {
			const orderForUi = vocabularies.map(vocab => vocab.id);
			const orderedKeys = vocabularies.map(vocab => vocab.info);
			const orderForDb = orderedKeys.join(',');
			props.onVocabOrderChanged(orderForDb, orderForUi, orderedKeys);
		}
	}, [vocabularies]);

	const disableEnrichmentTitle = localize(
		'If you select this setting, you will no longer see the semantic categories visualization at the top of your search results and you will no longer be presented with synonym matches when entering search terms.'
	);

	const tooltipTitle = localize(
		'Drag and drop categories into the order you wish them to display. This will determine the display order of these categories in your search results, as well as when you semantically enrich a document.'
	);

	const moveVocabulary = (from: number, to: number) => {
		const data = Array.from(vocabularies);
		const [removed] = data.splice(from, 1);
		data.splice(to, 0, removed);

		setVocabularies(data);
	};

	const onDragEnd = (event: IOnDragEndEventArgs) => {
		if (!event.destination) {
			return;
		}

		const from = event.source.index;
		const to = event.destination.index;
		moveVocabulary(from, to);
	};

	const onMoveToTop = (index: number) => {
		if (index > 0) {
			moveVocabulary(index, 0);
		}
	};

	return (
		<Box
			sx={{ opacity: props.isLoading ? 0.3 : 1, pointerEvents: props.isLoading ? 'none' : 'auto' }}
			display="flex"
			flexDirection="column"
			rowGap={4}
		>
			{switchable && (
				<Box display="flex" flexDirection="column" columnGap={1}>
					<RupSwitch
						style={{
							color: Colors.neutral900
						}}
						value={enrichmentEnabled}
						onChange={value => {
							setEnrichmentEnabled(value);

							if (props.onEnrichmentEnabledChanged) {
								props.onEnrichmentEnabledChanged(value);
							}
						}}
						label={localize('Semantic enrichment')}
						id={'semantic-entichment-settings'}
						small={true}
					/>
					<Box sx={tabsCaptionSx}>{disableEnrichmentTitle}</Box>
				</Box>
			)}
			<Collapse in={enrichmentEnabled || !switchable}>
				<Box display="flex" flexDirection="column" rowGap={2}>
					<Box
						sx={{ '& .rup-icon svg': { margin: 'auto', color: Colors.neutral700 } }}
						display="flex"
						columnGap={1}
					>
						<Box sx={tabsCaptionStrongSx}>{localize('Set priority order for categories')}</Box>
						<RupTooltip title={tooltipTitle} sx={{ maxWidth: '14rem' }} placement="bottom" arrow>
							<span
								className="rup-icon rup-icon--sm"
								dangerouslySetInnerHTML={{ __html: Icons.InfoIcon }}
							/>
						</RupTooltip>
					</Box>
					<Box>
						<DragDropContext onDragEnd={onDragEnd}>
							<Droppable droppableId="droppable">
								{(provided, _) => (
									<Stack
										rowGap={1}
										maxHeight="25rem"
										sx={{ overflowY: 'auto' }}
										{...provided.droppableProps}
										ref={provided.innerRef}
									>
										{vocabularies.map((item, index) => {
											const colorPalette = props.vocabColorPalette[item.id];
											return (
												<ColoredDraggableVocabulary
													key={index}
													palette={colorPalette}
													title={item.label}
													index={index}
													id={item.id.toString()}
													onMoveToTop={onMoveToTop}
												/>
											);
										})}
										{provided.placeholder}
									</Stack>
								)}
							</Droppable>
						</DragDropContext>
					</Box>
				</Box>
			</Collapse>
		</Box>
	);
};

export default EnrichmentSettings;
